import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Users, Clock, CheckCircle2, AlertTriangle, Navigation, Activity, Plus, RefreshCw, MapPin, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { patientApi } from '../../lib/api';

function tone(s) {
  if (s === 'completed') return { bg: '#DCFCE7', text: '#166534', label: 'Completed' };
  if (s === 'in_progress') return { bg: '#FEF3C7', text: '#92400E', label: 'In Progress' };
  if (s === 'missed') return { bg: '#FEE2E2', text: '#991B1B', label: 'Missed' };
  if (s === 'cancelled') return { bg: '#F1F5F9', text: '#475569', label: 'Cancelled' };
  return { bg: '#DBEAFE', text: '#1E40AF', label: 'Scheduled' };
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); } catch { return iso; }
}

function fmtDateTimeInputValue(dt) {
  // Returns YYYY-MM-DDTHH:mm for an <input type="datetime-local">
  if (!dt) return '';
  const d = new Date(dt);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function EVVDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [visits, setVisits] = useState([]);
  const [aides, setAides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [aideFilter, setAideFilter] = useState('all');
  const [showAssign, setShowAssign] = useState(false);
  const [detailVisit, setDetailVisit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, live, aidesRes] = await Promise.all([
        api.get('/admin/evv/overview'),
        api.get('/admin/evv/live'),
        api.get('/admin/evv/aides'),
      ]);
      setKpis(ov.data.kpis);
      setVisits(live.data || []);
      setAides(aidesRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load EVV data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh every 30s
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    return visits.filter(v => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (aideFilter !== 'all' && v.aide_id !== aideFilter) return false;
      return true;
    });
  }, [visits, statusFilter, aideFilter]);

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2342]" data-testid="evv-page-title">EVV Dashboard</h1>
          <p className="text-sm text-[#64748B]">Live home-care visits with GPS-verified clock in/out (NY Home Care EVV compliance)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="evv-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button onClick={() => setShowAssign(true)} className="careiq-btn-primary" data-testid="assign-visit-btn">
            <Plus className="w-4 h-4 mr-1.5" />Assign Visit
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard label="Today" value={kpis?.visits_today ?? 0} sub={`${kpis?.completed_today ?? 0} completed`} icon={<Activity className="w-4 h-4" />} color="#0E7490" />
        <KPICard label="In Progress" value={kpis?.in_progress ?? 0} sub="right now" icon={<Clock className="w-4 h-4" />} color="#F59E0B" />
        <KPICard label="GPS Verified (7d)" value={`${kpis?.gps_verification_pct ?? 0}%`} sub="clock in & out" icon={<Navigation className="w-4 h-4" />} color="#166534" />
        <KPICard label="Incidents (7d)" value={kpis?.incidents_7d ?? 0} sub={`${kpis?.active_aides ?? 0} active aides`} icon={<AlertTriangle className="w-4 h-4" />} color="#991B1B" />
      </div>

      {kpis?.missed > 0 && (
        <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl p-3 flex items-center gap-2 text-sm text-[#991B1B]" data-testid="missed-banner">
          <AlertTriangle className="w-4 h-4" />{kpis.missed} visit{kpis.missed === 1 ? '' : 's'} missed — scheduled end is past with no clock-in.
        </div>
      )}

      {/* Filters */}
      <Card className="careiq-card">
        <CardContent className="p-3 flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="evv-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="missed">Missed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={aideFilter} onValueChange={setAideFilter}>
            <SelectTrigger className="w-56 h-9" data-testid="evv-aide-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Aides</SelectItem>
              {aides.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="ml-auto text-xs text-[#64748B] self-center">{filtered.length} visit{filtered.length === 1 ? '' : 's'}</div>
        </CardContent>
      </Card>

      {/* Visits list */}
      <Card className="careiq-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Today's Visits &amp; Active</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <th className="table-header text-left pl-4">Aide</th>
                  <th className="table-header text-left">Patient</th>
                  <th className="table-header text-left">Scheduled</th>
                  <th className="table-header text-left">Clock In</th>
                  <th className="table-header text-left">Clock Out</th>
                  <th className="table-header text-left">GPS</th>
                  <th className="table-header text-left">Status</th>
                  <th className="table-header text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">Loading…</td></tr>}
                {!loading && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[#64748B]" data-testid="no-visits-row">No visits match the current filters.</td></tr>}
                {!loading && filtered.map(v => {
                  const t = tone(v.status);
                  const gpsOk = v.clock_in_location || v.clock_out_location;
                  return (
                    <tr key={v.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`evv-row-${v.id}`}>
                      <td className="px-4 py-2.5 text-[#0A2342]">{v.aide_name || '—'}</td>
                      <td className="px-4 py-2.5">{v.patient_name || '—'}</td>
                      <td className="px-4 py-2.5 text-[#475569]">{fmtTime(v.scheduled_start)} – {fmtTime(v.scheduled_end)}</td>
                      <td className="px-4 py-2.5 text-[#475569]">{fmtTime(v.clock_in_at)}</td>
                      <td className="px-4 py-2.5 text-[#475569]">{fmtTime(v.clock_out_at)}</td>
                      <td className="px-4 py-2.5">
                        {gpsOk ? <Navigation className="w-4 h-4 text-[#166534]" aria-label="GPS verified" /> : <span className="text-xs text-[#94A3B8]">—</span>}
                      </td>
                      <td className="px-4 py-2.5"><Badge style={{ backgroundColor: t.bg, color: t.text }}>{t.label}</Badge></td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setDetailVisit(v)} data-testid={`view-visit-${v.id}`}><Eye className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showAssign && (
        <AssignVisitDialog
          open={showAssign}
          onClose={() => setShowAssign(false)}
          aides={aides}
          onCreated={() => { setShowAssign(false); load(); }}
        />
      )}

      {detailVisit && (
        <VisitDetailDialog visit={detailVisit} onClose={() => setDetailVisit(null)} />
      )}
    </div>
  );
}

function KPICard({ label, value, sub, icon, color }) {
  return (
    <Card className="careiq-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-[#64748B] uppercase tracking-wide">{label}</div>
          <div style={{ color }}>{icon}</div>
        </div>
        <div className="text-2xl font-bold mt-1" style={{ color }}>{value}</div>
        <div className="text-xs text-[#64748B]">{sub}</div>
      </CardContent>
    </Card>
  );
}

function AssignVisitDialog({ open, onClose, aides, onCreated }) {
  const [aideId, setAideId] = useState('');
  const [patientId, setPatientId] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [start, setStart] = useState(fmtDateTimeInputValue(new Date()));
  const [end, setEnd] = useState(fmtDateTimeInputValue(new Date(Date.now() + 2 * 60 * 60 * 1000)));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    patientApi.list({ limit: 200 }).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.patients || []);
      setPatients(list);
    }).catch(() => setPatients([]));
  }, []);

  const filteredPatients = patients.filter(p => {
    if (!patientSearch) return true;
    const s = patientSearch.toLowerCase();
    return (`${p.first_name} ${p.last_name}`.toLowerCase().includes(s)) || (p.mrn || '').toLowerCase().includes(s);
  }).slice(0, 50);

  const submit = async () => {
    if (!aideId || !patientId || !start || !end) {
      toast.error('Select aide, patient, and schedule');
      return;
    }
    setSaving(true);
    try {
      await api.post('/care-logs/assignments', {
        aide_id: aideId,
        patient_id: patientId,
        scheduled_start: new Date(start).toISOString(),
        scheduled_end: new Date(end).toISOString(),
        notes,
      });
      toast.success('Visit assigned');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="assign-visit-dialog">
        <DialogHeader><DialogTitle>Assign Home-Care Visit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-[#0A2342] block mb-1">Aide</label>
            <Select value={aideId} onValueChange={setAideId}>
              <SelectTrigger className="careiq-input" data-testid="assign-aide-select"><SelectValue placeholder="Choose an aide…" /></SelectTrigger>
              <SelectContent>
                {aides.length === 0 && <div className="p-3 text-xs text-[#64748B]">No aides in your organization yet. Create an Aide user in /admin/users.</div>}
                {aides.map(a => <SelectItem key={a.id} value={a.id}>{a.name} — {a.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A2342] block mb-1">Patient</label>
            <Input
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              placeholder="Search patients by name or MRN…"
              className="careiq-input mb-2"
              data-testid="assign-patient-search"
            />
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger className="careiq-input" data-testid="assign-patient-select"><SelectValue placeholder="Choose a patient…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {filteredPatients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.mrn})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#0A2342] block mb-1">Start</label>
              <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="careiq-input" data-testid="assign-start" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0A2342] block mb-1">End</label>
              <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="careiq-input" data-testid="assign-end" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A2342] block mb-1">Notes (optional)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="careiq-input" data-testid="assign-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="careiq-btn-primary" data-testid="assign-save-btn">{saving ? 'Saving…' : 'Assign Visit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VisitDetailDialog({ visit, onClose }) {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get(`/care-logs/assignments/${visit.id}/events`).then(r => setEvents(r.data || [])).catch(() => {});
  }, [visit.id]);
  const cl = visit.care_log || {};
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="visit-detail-dialog">
        <DialogHeader><DialogTitle>Visit Detail — {visit.patient_name}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-[#64748B]">Aide:</span> <b>{visit.aide_name || '—'}</b></div>
            <div><span className="text-[#64748B]">Status:</span> <b>{visit.status}</b></div>
            <div><span className="text-[#64748B]">Scheduled:</span> {fmtTime(visit.scheduled_start)} – {fmtTime(visit.scheduled_end)}</div>
            <div><span className="text-[#64748B]">Actual:</span> {fmtTime(visit.clock_in_at)} – {fmtTime(visit.clock_out_at)}</div>
          </div>

          <div>
            <div className="font-semibold text-[#0A2342] mb-1">EVV Events</div>
            {events.length === 0 ? <div className="text-xs text-[#64748B]">No events yet.</div> : (
              <div className="space-y-1.5">
                {events.map(e => (
                  <div key={e.id} className="flex items-center gap-2 text-xs bg-[#F8FAFC] rounded p-2" data-testid={`evv-event-${e.id}`}>
                    <Badge variant="outline">{e.type === 'clock_in' ? 'IN' : 'OUT'}</Badge>
                    <span>{new Date(e.timestamp).toLocaleString()}</span>
                    {e.location ? (
                      <span className="text-[#166534] flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location.lat.toFixed(4)}, {e.location.lng.toFixed(4)} (±{Math.round(e.location.accuracy || 0)}m)</span>
                    ) : <span className="text-[#991B1B]">No GPS</span>}
                    <span className="ml-auto text-[#64748B]">{e.ip_address}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {visit.status === 'completed' && (
            <div className="space-y-3">
              <div>
                <div className="font-semibold text-[#0A2342] mb-1">Tasks Completed</div>
                {(cl.tasks_completed && cl.tasks_completed.length) ? (
                  <div className="flex flex-wrap gap-1.5">{cl.tasks_completed.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}</div>
                ) : <div className="text-xs text-[#64748B]">None recorded</div>}
              </div>
              {cl.vitals && Object.keys(cl.vitals).length > 0 && (
                <div>
                  <div className="font-semibold text-[#0A2342] mb-1">Vitals</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(cl.vitals).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><span className="text-[#64748B]">{k.toUpperCase()}:</span> <b>{v}</b></div>
                    ))}
                  </div>
                </div>
              )}
              {cl.general_notes && (
                <div>
                  <div className="font-semibold text-[#0A2342] mb-1">Notes</div>
                  <div className="text-xs bg-[#F8FAFC] rounded p-2 whitespace-pre-wrap">{cl.general_notes}</div>
                </div>
              )}
              {cl.incident_reported && (
                <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded p-3">
                  <div className="font-semibold text-[#991B1B] flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" />Incident Reported</div>
                  <div className="text-xs text-[#7F1D1D] whitespace-pre-wrap">{cl.incident_details || '(no details)'}</div>
                </div>
              )}
              {cl.signature_data_url && (
                <div>
                  <div className="font-semibold text-[#0A2342] mb-1">Client Signature {cl.signer_name ? `— ${cl.signer_name}` : ''}</div>
                  <img src={cl.signature_data_url} alt="Signature" className="border border-[#E2E8F0] rounded bg-white max-h-24" />
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
